import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import MainCalculatorsList from "../MainCalculators";
import RelatedLinks from "../../RelatedLinks";
import { SecondAds, FirstAds } from "../../../Ads";
import SharedHelmet from "../../Helmet";
import imgUrl from "../../../../images/age-year-fr.webp";

function GregorianYear() {
    const { year } = useParams();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();
    const [birthYear, setBirthYear] = useState(parseInt(year) || 2006);
    const [birthMonth, setBirthMonth] = useState(currentMonth);
    const [birthDay, setBirthDay] = useState(currentDay);
    const [result, setResult] = useState(null);

    const TITLE = `Quel âge ont les personnes nées en ${birthYear}`;
    const DESCRIPTION = `Calculez l'âge des personnes nées en ${birthYear} avec précision selon le calendrier grégorien, en années, mois et jours, avec une explication détaillée de leur âge actuel.`;
    const KEYWORDS = `quel âge ${birthYear}, calculer âge nés en ${birthYear}, nés en ${birthYear} quel âge ont-ils, âge nés ${birthYear} aujourd'hui, calculer âge nés ${birthYear}`;
    const OG_URL = `https://do-calculate.com/calculator/fr/gregorian/${birthYear}/`;

    useEffect(() => {
        if (year && parseInt(year) !== birthYear) {
            setBirthYear(parseInt(year));
        }
        calculateAge();
    }, [year, birthYear, birthMonth, birthDay]);

    const calculateAge = () => {
        const today = new Date();
        const birthDate = new Date(birthYear, birthMonth - 1, birthDay);
        let ageYears = today.getFullYear() - birthDate.getFullYear();
        let ageMonths = today.getMonth() - birthDate.getMonth();
        let ageDays = today.getDate() - birthDate.getDate();

        if (ageDays < 0) {
            ageMonths--;
            const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            ageDays += lastMonth.getDate();
        }
        if (ageMonths < 0) {
            ageYears--;
            ageMonths += 12;
        }

        const ageWeeks = Math.floor(ageDays / 7);
        const remainingDays = ageDays % 7;
        const diffTime = Math.abs(today - birthDate);
        const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        setResult({
            years: ageYears,
            months: ageMonths,
            weeks: ageWeeks,
            days: remainingDays,
            totalDays,
            beforeCutoff: `${birthYear}/${currentMonth}/${currentDay}`,
            afterCutoff: `${birthYear}/${currentMonth}/${currentDay + 1}`
        });
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        const numValue = parseInt(value) || 0;
        if (name === "day") {
            setBirthDay(Math.min(31, Math.max(1, numValue)));
        } else if (name === "month") {
            setBirthMonth(Math.min(12, Math.max(1, numValue)));
        }
    };

    return (
        <div>
            <SharedHelmet
                TITLE={TITLE}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
                IMAGE={imgUrl}
            />
            <div className="row">
                <div className="col-12 col-md-8">
                    <FirstAds />
                </div>
                <div className="col-12 col-md-4 text-center">
                    <img
                        src={imgUrl}
                        alt={"Quel âge ont les personnes nées en " + birthYear + " | Calculez votre âge avec précision"}
                        width="300px"
                        height="300px"
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-8">
                    <br />
                    <div className="bg-success text-white text-center" style={{ height: "50px" }}>
                        <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
                    </div>
                    <p>
                        Les personnes nées en {birthYear} se demandent souvent : quel est notre âge maintenant ?
                        Sur cette page, nous vous proposons un outil précis pour calculer l'âge des personnes
                        nées en {birthYear} selon le calendrier grégorien, avec les années, les mois et les jours,
                        accompagné d'une explication détaillée de leur âge actuel.
                        <br />
                        Que vous cherchiez "quel âge ont les personnes nées en {birthYear}" ou "nés en {birthYear}
                        quel âge ont-ils aujourd'hui", vous trouverez ici la réponse claire.
                        Cet outil vous aide à connaître l'âge réel de toute personne née cette année-là,
                        que ce soit pour un usage personnel ou officiel comme les inscriptions scolaires,
                        les emplois ou les services gouvernementaux.
                        <br />
                        Les personnes nées en {birthYear} ont actuellement des âges différents selon leur date
                        d'anniversaire exacte, mais pour calculer avec précision, l'outil compare la date actuelle
                        à la date de naissance directement.
                        Pour confirmation : quel est l'âge des personnes nées en {birthYear} aujourd'hui ?
                        Calculez maintenant gratuitement et sans aucune complication.
                    </p>
                    <div className="border border-light">
                        <div className="row">
                            <div className="col calculatorStyle">
                                <div className="p-3 mb-2 bg-success text-white">
                                    <h2 style={{ fontSize: "16px" }}>
                                        {TITLE} {" "}
                                        | Calculatrice nés en {birthYear}
                                    </h2>
                                </div>
                                <div className="row gx-2 gy-2">
                                    <div className="col-12 col-sm-4 d-flex flex-column">
                                        <label htmlFor="birthYear" className="mb-1">Année :</label>
                                        <input
                                            id="birthYear"
                                            type="number"
                                            value={birthYear}
                                            readOnly
                                            className="form-control text-center"
                                            disabled
                                            aria-label="Année de naissance"
                                        />
                                    </div>
                                    <div className="col-12 col-sm-4 d-flex flex-column">
                                        <label htmlFor="birthMonth" className="mb-1">Mois :</label>
                                        <input
                                            id="birthMonth"
                                            name="month"
                                            type="number"
                                            value={birthMonth}
                                            onChange={handleDateChange}
                                            min="1"
                                            max="12"
                                            className="form-control text-center"
                                            aria-label="Mois de naissance"
                                        />
                                    </div>
                                    <div className="col-12 col-sm-4 d-flex flex-column text-center">
                                        <label htmlFor="birthDay" className="mb-1">Jour :</label>
                                        <input
                                            id="birthDay"
                                            name="day"
                                            type="number"
                                            value={birthDay}
                                            onChange={handleDateChange}
                                            min="1"
                                            max="31"
                                            className="form-control text-center"
                                            aria-label="Jour de naissance"
                                        />
                                    </div>
                                </div>
                                {result && (
                                    <div className="p-3 mb-2 text-center">
                                        <p className="alert alert-success">
                                            L'âge des personnes nées en {birthYear} avant le {result.beforeCutoff} est <strong>{result.years} an(s)</strong>
                                        </p>
                                        <p className="alert alert-success">
                                            L'âge des personnes nées en {birthYear} après le {result.beforeCutoff} est <strong>{result.years - 1} an(s)</strong>
                                        </p>
                                        <hr className="bg-white" />
                                        <p className="alert alert-info">
                                            <b>Âge détaillé</b> : {result.years} an(s), {result.months} mois, {result.weeks} semaine(s), {result.days} jour(s)
                                        </p>
                                        <p className="alert alert-info"><b>Total des jours :</b> {result.totalDays} jours</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <hr />
                        <div className="row">
                            <RelatedLinks />
                        </div>
                    </div>
                </div>
                <div className="col">
                    <SecondAds />
                    <br />
                    <MainCalculatorsList />
                    <br />
                </div>
            </div>
            <hr />
            <div className="row" style={{ height: "auto" }}>
                <div className="col-12 col-md-8">
                    <div className="info-sections-container">
                        <div className="info-section">
                            <div className="info-section-header">
                                <h2 style={{ fontSize: '16px' }}>Quel âge ont les personnes nées en {birthYear} ? Explication du calcul de l'âge</h2>
                            </div>
                            <div className="info-section-body">
                                <div>
                                    <h3>Comment calculer l'âge des personnes nées en {birthYear}</h3>
                                    <p>
                                        Pour calculer l'âge d'une personne née en {birthYear}, on compare la date de
                                        naissance à la date actuelle. La méthode est la suivante :
                                    </p>
                                    <ul>
                                        <li>Si l'anniversaire est déjà passé : Âge = Année actuelle - {birthYear}</li>
                                        <li>Si l'anniversaire n'est pas encore passé : Âge = (Année actuelle - {birthYear}) - 1</li>
                                    </ul>
                                    <div className="p-3 bg-info text-dark">
                                        <h4>Exemples de calcul d'âge avec précision :</h4>
                                        <ul>
                                            <li>Date de naissance 01/01 : Âge = {currentYear - birthYear} an(s)</li>
                                            <li>Date de naissance 31/12 : Âge = {currentYear - birthYear - 1} an(s)</li>
                                        </ul>
                                    </div>
                                    <p>
                                        Quel âge ont les personnes nées en {birthYear} maintenant ? Cet outil vous aide à
                                        connaître l'âge en années, mois et jours.
                                    </p>
                                    <p>
                                        Vous vous demandez : quel âge ont les personnes nées en {birthYear} ? Ou quel âge
                                        ont-ils aujourd'hui ? Voici la réponse.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GregorianYear;
